/**
 * SiteAccessPanel — Lot 3, 3.1 : les responsables d'un site, à l'écran.
 *
 * Bloc de l'onglet « Responsables » de `SiteDetailModal` (l'intégrateur y empile ce panneau et
 * `SiteGrantPanel`, le manager de garde du 3.2 — ni l'un ni l'autre n'y touche seul).
 *
 * Liste les utilisateurs ayant un accès DIRECT sur ce site (retirable ici) et ceux qui l'ont
 * HÉRITÉ d'un ancêtre (grisé, non retirable — il n'a pas de ligne propre sur CE site :
 * il se retire depuis le site où il a été posé). « Ajouter un responsable » choisit un
 * utilisateur de l'org ; le retrait du DERNIER accès d'un utilisateur est refusé par le serveur
 * (`LAST_ACCESS`) — ce panneau relaie le refus, il ne le devine pas côté client.
 */
import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { UserPlus, X, Lock, AlertTriangle } from 'lucide-react';
import { siteOrgAPI } from '../../api/siteOrg.js';
import { userAPI } from '../../api/admin.js';

function ERROR_LABEL_KEY(code) {
  switch (code) {
    case 'LAST_ACCESS': return 'sites.access.errors.lastAccess';
    case 'SITE_VIOLATION': return 'sites.access.errors.forbidden';
    case 'USER_ORG_MISMATCH': return 'sites.access.errors.orgMismatch';
    default: return 'sites.access.errors.generic';
  }
}

function SiteAccessPanel({ siteId }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [error, setError] = useState(null);
  const [picking, setPicking] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['siteAccess', siteId],
    queryFn: () => siteOrgAPI.getAccess(siteId),
    enabled: siteId != null,
  });

  const { data: usersData } = useQuery({
    queryKey: ['usersForSiteAccess'],
    queryFn: () => userAPI.getAll(),
    enabled: picking,
  });

  const direct = data?.direct || [];
  const inherited = data?.inherited || [];
  const users = useMemo(() => usersData?.data || usersData || [], [usersData]);
  const alreadyGranted = useMemo(() => new Set(direct.map((a) => a.user_id)), [direct]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['siteAccess', siteId] });

  const grantMutation = useMutation({
    mutationFn: (userId) => siteOrgAPI.grantAccess(siteId, userId),
    onSuccess: () => {
      setError(null);
      setPicking(false);
      setSelectedUserId('');
      invalidate();
    },
    onError: (err) => setError(err?.code || 'generic'),
  });

  const revokeMutation = useMutation({
    mutationFn: (userId) => siteOrgAPI.revokeAccess(siteId, userId),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (err) => setError(err?.code || 'generic'),
  });

  if (isLoading) {
    return <p className="text-gray-500 text-center py-8">{t('common.states.loading')}</p>;
  }
  if (isError) {
    return <p className="text-red-500 text-center py-8">{t('sites.access.loadError')}</p>;
  }

  return (
    <div className="space-y-4" data-testid="site-access-panel">
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          {t(ERROR_LABEL_KEY(error))}
        </div>
      )}

      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2">{t('sites.access.directTitle')}</h4>
        {direct.length === 0 ? (
          <p className="text-gray-400 text-sm py-2">{t('sites.access.noneDirect')}</p>
        ) : (
          <ul className="space-y-2">
            {direct.map((a) => (
              <li key={a.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-800">{a.name || `#${a.user_id}`}</span>
                <button
                  type="button"
                  onClick={() => revokeMutation.mutate(a.user_id)}
                  disabled={revokeMutation.isPending}
                  className="p-1 text-gray-400 hover:text-red-600"
                  title={t('sites.access.revoke')}
                >
                  <X className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {inherited.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-500 mb-2">{t('sites.access.inheritedTitle')}</h4>
          <ul className="space-y-2">
            {inherited.map((a) => (
              <li key={a.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg opacity-60">
                <span className="text-sm text-gray-600 flex items-center gap-1.5">
                  <Lock className="w-3.5 h-3.5" /> {a.name || `#${a.user_id}`}
                </span>
                <span className="text-xs text-gray-400">{t('sites.access.inheritedBadge')}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div>
        {picking ? (
          <div className="flex items-center gap-2">
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">{t('sites.access.pickUser')}</option>
              {users
                .filter((u) => !alreadyGranted.has(u.id))
                .map((u) => (
                  <option key={u.id} value={u.id}>
                    {[u.first_name, u.last_name].filter(Boolean).join(' ') || u.email}
                  </option>
                ))}
            </select>
            <button
              type="button"
              disabled={!selectedUserId || grantMutation.isPending}
              onClick={() => grantMutation.mutate(Number(selectedUserId))}
              className="px-3 py-2 bg-purple-600 text-white rounded-lg text-sm disabled:opacity-50"
            >
              {t('common.actions.add')}
            </button>
            <button
              type="button"
              onClick={() => { setPicking(false); setSelectedUserId(''); }}
              className="px-3 py-2 text-gray-500 text-sm"
            >
              {t('common.actions.cancel')}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setPicking(true)}
            className="flex items-center gap-1.5 text-sm text-purple-600 hover:text-purple-800 font-medium"
          >
            <UserPlus className="w-4 h-4" /> {t('sites.access.addManager')}
          </button>
        )}
      </div>

      <p className="text-xs text-gray-400 pt-2 border-t border-gray-100">{t('sites.access.effectiveNextLogin')}</p>
    </div>
  );
}

export default SiteAccessPanel;
